// src/components/ExpertCertificationProgress.jsx
import React, { useEffect, useState, useCallback } from "react";
import { getAccessToken } from "@/lib/supabaseClient";
import { Award, Star, Users, AlertCircle, RefreshCw } from "lucide-react";

export default function ExpertCertificationProgress({ expertId, expertRow }) {
  const [loading, setLoading] = useState(true);
  const [validationData, setValidationData] = useState(null);
  const [error, setError] = useState(null);

  const loadValidationCount = useCallback(async () => {
    if (!expertId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Récupérer le token d'authentification
      const token = await getAccessToken();
      
      if (!token) {
        console.warn("⚠️ [certification] No access token");
        setError("Session expirée - Reconnectez-vous");
        setLoading(false);
        return;
      }

      console.log("✅ [certification] Token obtained, calling Edge Function...");
      console.log("📤 [certification] expert_id:", expertId);

      // Appel direct avec fetch pour un contrôle total
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const functionUrl = `${supabaseUrl}/functions/v1/get-validation-count`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': anonKey
        },
        body: JSON.stringify({ expert_id: expertId })
      });

      console.log("📥 [certification] Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ [certification] HTTP error:", response.status, errorText);
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ [certification] Data received:", data);

      setValidationData(data || {
        validation_count: 0,
        certified: false,
        validations: []
      });
    } catch (e) {
      console.error("💥 [certification] Error:", e);
      
      if (e.message?.includes("401")) {
        setError("Session expirée - Reconnectez-vous");
      } else if (e.message?.includes("403")) {
        setError("Accès non autorisé");
      } else if (e.message?.includes("404")) {
        setError("Expert non trouvé");
      } else {
        setError("Impossible de charger les validations");
      }
    } finally {
      setLoading(false);
    }
  }, [expertId]);

  useEffect(() => {
    if (!expertId || !expertRow?.is_public) {
      setLoading(false);
      return;
    }
    loadValidationCount();
  }, [expertId, expertRow?.is_public, loadValidationCount]);

  // ===== CAS 1: Expert pas encore créé =====
  if (!expertId) {
    return null;
  }

  // ===== CAS 2: Expert privé =====
  if (!expertRow?.is_public) {
    return (
      <div style={{
        padding: 20,
        background: "#fafaf8",
        border: "1px solid #e5e3df",
        borderRadius: 2,
        textAlign: "center"
      }}>
        <div style={{
          fontFamily: "system-ui, sans-serif",
          fontSize: 13,
          color: "#6b6b6b",
          marginBottom: 12
        }}>
          🔒 Certification disponible après activation de votre présence publique
        </div>
        <div style={{
          fontSize: 12,
          color: "#9ca3af",
          fontFamily: "system-ui, sans-serif"
        }}>
          Activez le toggle "Présence publique Artkiv" ci-dessus pour débloquer cette fonctionnalité
        </div>
      </div>
    );
  }

  // ===== CAS 3: Chargement =====
  if (loading) {
    return (
      <div style={{
        padding: 20,
        background: "#fafaf8",
        border: "1px solid #e5e3df",
        borderRadius: 2,
        textAlign: "center",
        color: "#6b6b6b",
        fontFamily: "system-ui, sans-serif",
        fontSize: 13
      }}>
        Chargement des validations...
      </div>
    );
  }

  // ===== CAS 4: Erreur =====
  if (error) {
    return (
      <div style={{
        padding: 20,
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 2,
        color: "#991b1b",
        fontFamily: "system-ui, sans-serif",
        fontSize: 13
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <AlertCircle size={16} />
          <span style={{ fontWeight: 600 }}>{error}</span>
        </div>
        <button
          onClick={loadValidationCount}
          style={{
            padding: "8px 16px",
            background: "#fff",
            color: "#991b1b",
            border: "1px solid #fecaca",
            borderRadius: 2,
            cursor: "pointer",
            fontFamily: "system-ui, sans-serif",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          <RefreshCw size={14} />
          Réessayer
        </button>
      </div>
    );
  }

  const count = validationData?.validation_count || 0;
  const certified = validationData?.certified || false;
  const remaining = Math.max(0, 3 - count);
  const averageRating = validationData?.average_rating;
  const validations = validationData?.validations || [];

  // ===== CAS 5: Expert certifié (>= 3 validations) =====
  if (certified || count >= 3) {
    return (
      <div style={{
        background: "#ecfdf5",
        border: "1px solid #d1fae5",
        borderRadius: 2,
        padding: 24
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "#065f46",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <Award size={24} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <div style={{
              fontFamily: "Georgia, serif",
              fontSize: 18,
              fontWeight: 500,
              color: "#065f46",
              marginBottom: 4
            }}>
              Expert Certifié Artkiv
            </div>
            <div style={{
              fontFamily: "system-ui, sans-serif",
              fontSize: 13,
              color: "#047857"
            }}>
              Validé par {count} {count > 1 ? "collectionneurs" : "collectionneur"}
            </div>
          </div>
        </div>

        {averageRating && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingTop: 16,
            borderTop: "1px solid #d1fae5"
          }}>
            <Star size={16} color="#065f46" fill="#065f46" />
            <span style={{
              fontFamily: "system-ui, sans-serif",
              fontSize: 13,
              color: "#065f46",
              fontWeight: 500
            }}>
              Note moyenne: {averageRating}/5
            </span>
          </div>
        )}

        {validations.length > 0 && (
          <>
            <button
              onClick={() => {
                const details = document.getElementById(`validation-details-${expertId}`);
                if (details) {
                  details.style.display = details.style.display === "none" ? "block" : "none";
                }
              }}
              style={{
                marginTop: 12,
                padding: "8px 12px",
                background: "transparent",
                color: "#065f46",
                border: "1px solid #d1fae5",
                borderRadius: 2,
                cursor: "pointer",
                fontFamily: "system-ui, sans-serif",
                fontSize: 12,
                fontWeight: 500,
                width: "100%"
              }}
            >
              Voir les validations ({count})
            </button>

            <div id={`validation-details-${expertId}`} style={{ display: "none", marginTop: 16 }}>
              {validations.map((val) => (
                <div
                  key={val.id}
                  style={{
                    padding: 12,
                    background: "#fff",
                    border: "1px solid #d1fae5",
                    borderRadius: 2,
                    marginBottom: 8,
                    fontSize: 12,
                    fontFamily: "system-ui, sans-serif"
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4, color: "#065f46" }}>
                    {val.validator?.first_name} {val.validator?.last_name}
                  </div>
                  <div style={{ color: "#6b6b6b", fontSize: 11 }}>
                    {new Date(val.validated_at).toLocaleDateString("fr-FR")}
                  </div>
                  {val.rating && (
                    <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                      {Array.from({ length: val.rating }, (_, i) => (
                        <Star key={i} size={12} fill="#065f46" color="#065f46" />
                      ))}
                    </div>
                  )}
                  {val.validation_note && (
                    <div style={{ marginTop: 6, fontStyle: "italic", color: "#4a4a4a" }}>
                      "{val.validation_note}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ===== CAS 6: En cours de certification (0-2 validations) =====
  return (
    <div style={{
      background: "#fafaf8",
      border: "1px solid #e5e3df",
      borderRadius: 2,
      padding: 24
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16
      }}>
        <div>
          <div style={{
            fontFamily: "Georgia, serif",
            fontSize: 16,
            fontWeight: 400,
            color: "#1a1a1a",
            marginBottom: 4
          }}>
            Progression vers la certification
          </div>
          <div style={{
            fontFamily: "system-ui, sans-serif",
            fontSize: 12,
            color: "#6b6b6b"
          }}>
            {count}/3 validations · Plus que {remaining} {remaining > 1 ? "validations" : "validation"}
          </div>
        </div>
        <Users size={32} color="#d4d4d4" strokeWidth={1.5} />
      </div>

      {/* Barre de progression */}
      <div style={{
        display: "flex",
        gap: 6,
        marginBottom: 16
      }}>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 10,
              borderRadius: 2,
              background: count >= i ? "#2c5f2d" : "#e5e3df",
              transition: "all 0.3s ease"
            }}
          />
        ))}
      </div>

      {/* Explication */}
      <div style={{
        padding: 12,
        background: "#fff",
        border: "1px solid #e5e3df",
        borderRadius: 2,
        fontSize: 12,
        fontFamily: "system-ui, sans-serif",
        color: "#4a4a4a",
        lineHeight: 1.6
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#1a1a1a" }}>
          Comment obtenir le badge "Certifié" ?
        </div>
        Partagez votre profil avec vos clients Artkiv. Dès que 3 collectionneurs confirment 
        la qualité de vos services, vous obtenez automatiquement le badge.
      </div>

      {/* Validations existantes */}
      {count > 0 && validations.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#1a1a1a",
            marginBottom: 8,
            fontFamily: "system-ui, sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>
            Validations reçues ({count})
          </div>
          {validations.map((val) => (
            <div
              key={val.id}
              style={{
                padding: 10,
                background: "#fff",
                border: "1px solid #e5e3df",
                borderRadius: 2,
                marginBottom: 6,
                fontSize: 12,
                fontFamily: "system-ui, sans-serif"
              }}
            >
              <div style={{ fontWeight: 500, color: "#1a1a1a", marginBottom: 2 }}>
                {val.validator?.first_name} {val.validator?.last_name}
              </div>
              <div style={{ color: "#6b6b6b", fontSize: 11 }}>
                {new Date(val.validated_at).toLocaleDateString("fr-FR")}
              </div>
              {val.rating && (
                <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                  {Array.from({ length: val.rating }, (_, i) => (
                    <Star key={i} size={10} fill="#d4af37" color="#d4af37" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}